import React from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_SITE_NAME = 'StartEase Agent';
const DEFAULT_OG_IMAGE = '/og-default.png';
const SITE_ORIGIN = (process.env.REACT_APP_PUBLIC_SITE_URL || '').replace(/\/$/, '');

/**
 * SEOHead - Base reusable SEO component
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description
 * @param {string} [props.siteName='StartEase Agent'] - Site name for title suffix
 * @param {string} [props.canonical] - Canonical URL
 * @param {string} [props.ogTitle] - Open Graph title
 * @param {string} [props.ogDescription] - Open Graph description
 * @param {string} [props.ogImage] - Open Graph image URL
 * @param {string} [props.ogType='website'] - Open Graph type
 * @param {boolean} [props.noindex=false] - Whether to add noindex tag
 * @param {string} [props.author] - Content author
 * @param {string} [props.keywords] - Meta keywords
 * @param {string} [props.twitterCard='summary_large_image'] - Twitter card type
 * @param {React.ReactNode} [props.children] - Additional meta tags
 */
export const SEOHead = ({
  title,
  description,
  siteName = DEFAULT_SITE_NAME,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  noindex = false,
  author,
  keywords,
  twitterCard = 'summary_large_image',
  children,
}) => {
  // Avoid title duplication - don't append site name if already present
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  const effectiveOgTitle = ogTitle || title;
  const effectiveOgDescription = ogDescription || description;
  const effectiveOgImage = ogImage || DEFAULT_OG_IMAGE;
  const absoluteOgImage = effectiveOgImage.startsWith('http') ? effectiveOgImage : `${SITE_ORIGIN}${effectiveOgImage}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Author */}
      {author && <meta name="author" content={author} />}
      
      {/* Keywords */}
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={effectiveOgTitle} />
      <meta property="og:description" content={effectiveOgDescription} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={effectiveOgTitle} />
      {canonical && <meta property="og:url" content={canonical} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={effectiveOgTitle} />
      <meta name="twitter:description" content={effectiveOgDescription} />
      <meta name="twitter:image" content={absoluteOgImage} />
      
      {/* Additional custom meta tags passed as children */}
      {children}
    </Helmet>
  );
};

export default SEOHead;
